/*
 * Copyright 2024 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/mobile-wallet/blob/master/LICENSE.md
 */
package org.mifospay.feature.autopay

import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import org.mifospay.core.common.DataState
import org.mifospay.core.common.getSerialized
import org.mifospay.core.data.util.BillerValidator
import org.mifospay.core.datastore.BillerRepository
import org.mifospay.core.model.autopay.Biller
import org.mifospay.core.model.autopay.BillerCategory
import org.mifospay.core.model.autopay.BillerFormData
import org.mifospay.core.model.autopay.BillerValidationResult
import org.mifospay.core.ui.utils.BaseViewModel

class EditBillerViewModel(
    savedStateHandle: SavedStateHandle,
    private val billerRepository: BillerRepository,
) : BaseViewModel<EditBillerState, EditBillerEvent, EditBillerAction>(
    initialState = savedStateHandle.getSerialized(KEY_STATE) ?: EditBillerState(),
) {

    private val billerId: String = savedStateHandle["billerId"] ?: ""

    init {
        loadBiller()
    }

    override fun handleAction(action: EditBillerAction) {
        when (action) {
            is EditBillerAction.UpdateBillerName -> {
                updateBillerName(action.name)
            }
            is EditBillerAction.UpdateAccountNumber -> {
                updateAccountNumber(action.accountNumber)
            }
            is EditBillerAction.UpdateContactNumber -> {
                updateContactNumber(action.contactNumber)
            }
            is EditBillerAction.UpdateEmail -> {
                updateEmail(action.email)
            }
            is EditBillerAction.UpdateCategory -> {
                updateCategory(action.category)
            }
            is EditBillerAction.UpdateAddress -> {
                updateAddress(action.address)
            }
            is EditBillerAction.UpdateBiller -> {
                updateBiller()
            }
            is EditBillerAction.ValidateForm -> {
                validateForm()
            }
            is EditBillerAction.ClearError -> {
                clearError()
            }
            is EditBillerAction.ClearValidationErrors -> {
                clearValidationErrors()
            }
        }
    }

    private fun loadBiller() {
        viewModelScope.launch {
            mutableStateFlow.update { it.copy(isLoading = true) }

            try {
                val biller = billerRepository.getBillerById(billerId)
                if (biller != null) {
                    val formData = BillerFormData(
                        name = biller.name,
                        accountNumber = biller.accountNumber,
                        contactNumber = biller.contactNumber,
                        email = biller.email ?: "",
                        category = biller.category,
                        address = biller.address ?: "",
                    )
                    mutableStateFlow.update {
                        it.copy(
                            formData = formData,
                            isLoading = false,
                            error = null,
                        )
                    }
                } else {
                    mutableStateFlow.update {
                        it.copy(
                            isLoading = false,
                            error = "Biller not found",
                        )
                    }
                }
            } catch (e: Exception) {
                mutableStateFlow.update {
                    it.copy(
                        isLoading = false,
                        error = "Failed to load biller: ${e.message}",
                    )
                }
            }
        }
    }

    private fun updateBillerName(name: String) {
        val nameError = BillerValidator.validateNameField(name)
        mutableStateFlow.update {
            it.copy(
                formData = it.formData.copy(name = name),
                validationResult = it.validationResult.copy(nameError = nameError),
            )
        }
    }

    private fun updateAccountNumber(accountNumber: String) {
        val accountNumberError = BillerValidator.validateAccountNumberField(accountNumber)
        mutableStateFlow.update {
            it.copy(
                formData = it.formData.copy(accountNumber = accountNumber),
                validationResult = it.validationResult.copy(accountNumberError = accountNumberError),
            )
        }
    }

    private fun updateContactNumber(contactNumber: String) {
        val contactNumberError = BillerValidator.validateContactNumberField(contactNumber)
        mutableStateFlow.update {
            it.copy(
                formData = it.formData.copy(contactNumber = contactNumber),
                validationResult = it.validationResult.copy(contactNumberError = contactNumberError),
            )
        }
    }

    private fun updateEmail(email: String) {
        val emailError = BillerValidator.validateEmailField(email)
        mutableStateFlow.update {
            it.copy(
                formData = it.formData.copy(email = email),
                validationResult = it.validationResult.copy(emailError = emailError),
            )
        }
    }

    private fun updateCategory(category: BillerCategory) {
        mutableStateFlow.update {
            it.copy(
                formData = it.formData.copy(category = category),
                validationResult = it.validationResult.copy(categoryError = null),
            )
        }
    }

    private fun updateAddress(address: String) {
        mutableStateFlow.update {
            it.copy(
                formData = it.formData.copy(address = address),
            )
        }
    }

    private fun validateForm(): BillerValidationResult {
        val formData = mutableStateFlow.value.formData

        val validationResult = BillerValidator.validateBillerForm(formData)

        mutableStateFlow.update {
            it.copy(validationResult = validationResult)
        }

        return validationResult
    }

    private fun updateBiller() {
        val validationResult = validateForm()
        if (!validationResult.isValid) {
            return
        }

        viewModelScope.launch {
            mutableStateFlow.update { it.copy(isLoading = true) }

            try {
                val formData = mutableStateFlow.value.formData
                val biller = Biller(
                    id = billerId,
                    name = formData.name,
                    accountNumber = formData.accountNumber,
                    contactNumber = formData.contactNumber,
                    email = formData.email.takeIf { it.isNotBlank() },
                    category = formData.category!!,
                    address = formData.address.takeIf { it.isNotBlank() },
                )

                when (val result = billerRepository.updateBiller(biller)) {
                    is DataState.Success -> {
                        sendEvent(EditBillerEvent.BillerUpdated(result.data))
                    }
                    is DataState.Error -> {
                        mutableStateFlow.update {
                            it.copy(
                                isLoading = false,
                                error = "Failed to update biller: ${result.exception.message}",
                            )
                        }
                    }
                    is DataState.Loading -> {
                        // Loading state is already handled by setting isLoading = true above
                    }
                }
            } catch (e: Exception) {
                mutableStateFlow.update {
                    it.copy(
                        isLoading = false,
                        error = "Failed to update biller: ${e.message}",
                    )
                }
            }
        }
    }

    private fun clearError() {
        mutableStateFlow.update { it.copy(error = null) }
    }

    private fun clearValidationErrors() {
        mutableStateFlow.update {
            it.copy(
                validationResult = BillerValidationResult(isValid = false),
            )
        }
    }

    companion object {
        private const val KEY_STATE = "edit_biller_state"
    }
}

@Serializable
data class EditBillerState(
    val formData: BillerFormData = BillerFormData(),
    val validationResult: BillerValidationResult = BillerValidationResult(isValid = false),
    val isLoading: Boolean = false,
    val error: String? = null,
)

sealed interface EditBillerEvent {
    data class BillerUpdated(val biller: Biller) : EditBillerEvent
}

sealed interface EditBillerAction {
    data class UpdateBillerName(val name: String) : EditBillerAction
    data class UpdateAccountNumber(val accountNumber: String) : EditBillerAction
    data class UpdateContactNumber(val contactNumber: String) : EditBillerAction
    data class UpdateEmail(val email: String) : EditBillerAction
    data class UpdateCategory(val category: BillerCategory) : EditBillerAction
    data class UpdateAddress(val address: String) : EditBillerAction
    data object UpdateBiller : EditBillerAction
    data object ValidateForm : EditBillerAction
    data object ClearError : EditBillerAction
    data object ClearValidationErrors : EditBillerAction
}
